import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NAS_MODES, type NasBrowseResult, type NasMode, type NasStatus } from '@jelly-dl/shared';
import { api, ApiError } from '../api/client';
import { Button, Card, ErrorState, Field, Input, Spinner, Toggle } from '../components/ui';
import { humanBytes } from '../lib/format';

// ── Flow animation keyframes (scoped to this page; no global CSS dependency) ────
// Packets translate across the conduit on a continuous loop. Distance is driven
// by a CSS var so the same keyframes work at any panel width. prefers-reduced-motion
// slows everything to a crawl (effectively "stopped" feeling) for accessibility.
const FLOW_STYLE = `
@keyframes ls-packet {
  0%   { transform: translateX(0)            scale(0.85); opacity: 0; }
  8%   { opacity: 1; }
  92%  { opacity: 1; }
  100% { transform: translateX(var(--ls-travel)) scale(1); opacity: 0; }
}
@keyframes ls-nas-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(217, 70, 239, 0.0); transform: translateY(0); }
  50%      { box-shadow: 0 0 22px 4px rgba(217, 70, 239, 0.45); transform: translateY(-1px); }
}
@keyframes ls-flow-dash {
  to { background-position: 200% 0; }
}
.ls-packet {
  animation-name: ls-packet;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
.ls-nas-live { animation: ls-nas-pulse 1.1s ease-in-out infinite; }
.ls-conduit-flow {
  background-size: 200% 100%;
  animation: ls-flow-dash 1.4s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .ls-packet { animation-duration: 12s !important; }
  .ls-nas-live { animation: none; }
  .ls-conduit-flow { animation-duration: 8s; }
}
`;

const INDIGO = '#6366f1';
const FUCHSIA = '#d946ef';

export function LibrarySync() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['nas-status'],
    queryFn: () => api.get<NasStatus>('/api/nas/status'),
    // Poll fast while a transfer is running, otherwise back off to 3s.
    refetchInterval: (query) => (query.state.data?.sync.state === 'running' ? 1_000 : 3_000),
  });

  if (status.isLoading) return <Spinner label="Loading sync status…" />;
  if (status.isError || !status.data)
    return <ErrorState message="Couldn't load the NAS sync status." onRetry={() => status.refetch()} />;

  return <LibrarySyncView data={status.data} qc={qc} />;
}

function LibrarySyncView({
  data,
  qc,
}: {
  data: NasStatus;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const refetch = () => qc.invalidateQueries({ queryKey: ['nas-status'] });

  const running = data.sync.state === 'running';
  const [actionError, setActionError] = useState('');

  const sync = useMutation({
    mutationFn: () => api.post<NasStatus>('/api/nas/sync'),
    onSuccess: (next) => {
      setActionError('');
      qc.setQueryData(['nas-status'], next);
      refetch();
    },
    onError: (e) => setActionError(e instanceof ApiError ? e.message : 'Could not start the sync.'),
  });
  const cancel = useMutation({
    mutationFn: () => api.post<{ ok: true }>('/api/nas/sync/cancel'),
    onSuccess: () => refetch(),
    onError: (e) => setActionError(e instanceof ApiError ? e.message : 'Could not cancel the sync.'),
  });

  return (
    <div className="space-y-6">
      <style>{FLOW_STYLE}</style>

      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Library Sync</h1>
        <span className="text-sm text-slate-500">
          {data.nasMode === 'move' ? 'Moving' : 'Copying'} finished files to the NAS
        </span>
      </div>

      <FlowScene data={data} />

      <ControlsPanel
        data={data}
        refetch={refetch}
        running={running}
        sync={{
          start: () => sync.mutate(),
          starting: sync.isPending,
          cancel: () => cancel.mutate(),
          cancelling: cancel.isPending,
          error: actionError,
        }}
      />
    </div>
  );
}

// ── 1. The flow animation ──────────────────────────────────────────────────────
function FlowScene({ data }: { data: NasStatus }) {
  const { pending, sync } = data;
  const running = sync.state === 'running';
  const hasPending = pending.files > 0;

  // Idle = few, slow, dim packets ("ready" feel). Running = many, fast, bright.
  const active = running && sync.bytesTotal > 0;
  const packetCount = active ? 9 : 6;
  const duration = active ? 1.0 : 3.0;
  const packetOpacity = active ? 1 : hasPending ? 0.8 : 0.4;

  const filePct =
    sync.filesTotal > 0 ? Math.round((sync.filesDone / sync.filesTotal) * 100) : 0;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-b from-slate-950 to-slate-900/80 p-5 shadow-inner">
      <div className="relative flex h-[220px] items-stretch gap-3">
        {/* Left node — app output */}
        <SourceNode pending={pending} />

        {/* Conduit + packets */}
        <div className="relative flex min-w-0 flex-1 items-center">
          {/* glowing endpoints behind the pipe */}
          <div
            className="absolute left-0 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full"
            style={{ background: INDIGO, boxShadow: `0 0 14px 3px ${INDIGO}` }}
          />
          <div
            className="absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full"
            style={{ background: FUCHSIA, boxShadow: `0 0 14px 3px ${FUCHSIA}` }}
          />
          {/* the pipe */}
          <div className="relative h-12 w-full">
            <div
              className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full opacity-30"
              style={{ background: `linear-gradient(90deg, ${INDIGO}, ${FUCHSIA})` }}
            />
            <div
              className={`absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full ${
                active ? 'ls-conduit-flow opacity-90' : 'opacity-40'
              }`}
              style={{
                backgroundImage: `linear-gradient(90deg, transparent, ${INDIGO}, ${FUCHSIA}, transparent)`,
              }}
            />
            {/* travelling packets */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{ ['--ls-travel' as string]: 'calc(100% - 14px)' }}
            >
              {Array.from({ length: packetCount }).map((_, i) => (
                <span
                  key={i}
                  className="ls-packet absolute top-1/2 block h-3.5 w-3.5 -translate-y-1/2 rounded-[4px]"
                  style={{
                    left: 0,
                    background: i % 2 === 0 ? INDIGO : FUCHSIA,
                    boxShadow: `0 0 10px 1px ${i % 2 === 0 ? INDIGO : FUCHSIA}`,
                    opacity: packetOpacity,
                    animationDuration: `${duration}s`,
                    animationDelay: `${-(i * duration) / packetCount}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right node — NAS */}
        <DestNode data={data} running={running} />
      </div>

      {/* Running: progress beneath the scene */}
      {running && (
        <div className="mt-4 space-y-1.5">
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${filePct}%`,
                background: `linear-gradient(90deg, ${INDIGO}, ${FUCHSIA})`,
              }}
            />
          </div>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-400">
            <span>
              {sync.filesDone} / {sync.filesTotal} files · {humanBytes(sync.bytesDone)} /{' '}
              {humanBytes(sync.bytesTotal)}
            </span>
            <span className="min-w-0 truncate text-slate-500">{sync.currentFile ?? '…'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function SourceNode({ pending }: { pending: NasStatus['pending'] }) {
  return (
    <div className="flex w-44 flex-col items-center justify-center gap-2 rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-4 text-center">
      <FolderIcon />
      <div className="text-sm font-medium text-slate-100">jelly-dl · output</div>
      <div className="text-[11px] leading-tight text-slate-400">
        {pending.movies} movies · {pending.episodes} episodes
      </div>
      <div className="text-[11px] text-slate-500">{humanBytes(pending.totalBytes)} pending</div>
    </div>
  );
}

function DestNode({ data, running }: { data: NasStatus; running: boolean }) {
  const reachable = data.destReachable;
  return (
    <div
      className={`flex w-44 flex-col items-center justify-center gap-2 rounded-lg border px-3 py-4 text-center ${
        reachable ? 'border-fuchsia-900/50 bg-slate-900/70' : 'border-amber-900/50 bg-slate-900/70'
      } ${running ? 'ls-nas-live' : ''}`}
    >
      <NasIcon reachable={reachable} />
      <div className="text-sm font-medium text-slate-100">NAS · Jellyfin</div>
      <div className="w-full truncate text-[11px] leading-tight text-slate-400" title={data.nasPath}>
        {data.nasPath || '(no path set)'}
      </div>
      {reachable ? (
        <div className="text-[11px] text-slate-500">{humanBytes(data.destFreeBytes ?? 0)} free</div>
      ) : (
        <div className="text-[11px] font-medium text-amber-400">not reachable</div>
      )}
    </div>
  );
}

// ── 2. Controls panel ──────────────────────────────────────────────────────────
interface SyncControl {
  start: () => void;
  starting: boolean;
  cancel: () => void;
  cancelling: boolean;
  error: string;
}

function ControlsPanel({
  data,
  refetch,
  running,
  sync,
}: {
  data: NasStatus;
  refetch: () => void;
  running: boolean;
  sync: SyncControl;
}) {
  const qc = useQueryClient();
  const [saveError, setSaveError] = useState('');

  const patch = useMutation({
    mutationFn: (body: { nasPath?: string; nasMode?: NasMode; nasAutoSync?: boolean }) =>
      api.patch('/api/settings', body),
    onSuccess: () => {
      setSaveError('');
      refetch();
    },
    onError: (e) => setSaveError(e instanceof ApiError ? e.message : 'Could not save.'),
  });

  // Local, editable copy of the path; reseed when the server value changes.
  const [pathInput, setPathInput] = useState(data.nasPath);
  useEffect(() => {
    setPathInput(data.nasPath);
  }, [data.nasPath]);
  const pathDirty = pathInput.trim() !== data.nasPath;

  const [browseOpen, setBrowseOpen] = useState(false);

  const savePath = (p: string) => {
    setPathInput(p);
    patch.mutate({ nasPath: p });
  };

  const canSync = !running && data.pending.files > 0 && data.destWritable;
  const empty = data.pending.files === 0 && !running;

  return (
    <Card className="space-y-6">
      {/* Destination path */}
      <Field
        label="Destination (NAS) path"
        hint="Where finished movies & episodes are pushed for Jellyfin to pick up."
      >
        <div className="flex gap-2">
          <Input
            value={pathInput}
            onChange={(e) => setPathInput(e.target.value)}
            placeholder="/mnt/nas/media"
            spellCheck={false}
          />
          <Button variant="ghost" type="button" onClick={() => setBrowseOpen(true)}>
            Browse…
          </Button>
          <Button
            type="button"
            disabled={!pathDirty || patch.isPending}
            onClick={() => savePath(pathInput.trim())}
          >
            Save
          </Button>
        </div>
      </Field>
      <div className="-mt-3 flex items-center gap-3 text-xs">
        <WritableIndicator reachable={data.destReachable} writable={data.destWritable} />
        {saveError && <span className="text-rose-400">{saveError}</span>}
      </div>

      {browseOpen && (
        <BrowseModal
          initialPath={pathInput || data.nasPath}
          onClose={() => setBrowseOpen(false)}
          onSelect={(p) => {
            setBrowseOpen(false);
            savePath(p);
          }}
        />
      )}

      {/* Transfer mode */}
      <Field
        label="Transfer mode"
        hint="Move = no local copy left (recommended). Copy = keep a copy in the app's output too."
      >
        <SegmentedMode
          value={data.nasMode}
          disabled={patch.isPending}
          onChange={(m) => patch.mutate({ nasMode: m })}
        />
      </Field>

      {/* Auto-sync */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-slate-300">Auto-sync</div>
          <div className="text-xs text-slate-500">
            Automatically push each finished import to the NAS.
          </div>
        </div>
        <Toggle
          checked={data.nasAutoSync}
          onChange={(v) => patch.mutate({ nasAutoSync: v })}
          label="Auto-sync"
        />
      </div>

      <div className="border-t border-slate-800 pt-5">
        {running ? (
          <RunningPanel data={data} sync={sync} />
        ) : (
          <div className="space-y-3">
            <Button
              type="button"
              className="w-full py-3 text-base"
              disabled={!canSync || sync.starting}
              onClick={sync.start}
            >
              {sync.starting ? 'Starting…' : '🛰  Sync to NAS now'}
            </Button>
            {sync.error && <p className="text-sm text-rose-400">{sync.error}</p>}
            {empty ? (
              <p className="text-center text-sm text-slate-400">
                Everything&apos;s on the NAS ✓ — nothing to sync.
              </p>
            ) : !data.destWritable ? (
              <p className="text-center text-sm text-amber-400">
                Destination isn&apos;t writable — check the NAS path above.
              </p>
            ) : null}
            {data.sync.state === 'error' && data.sync.error && (
              <p className="text-sm text-rose-400">Last sync failed: {data.sync.error}</p>
            )}
            {data.sync.state === 'done' && data.sync.lastResult && (
              <p className="text-center text-sm text-emerald-400">{data.sync.lastResult}</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function RunningPanel({ data, sync }: { data: NasStatus; sync: SyncControl }) {
  const { sync: prog } = data;
  const pct = prog.filesTotal > 0 ? Math.round((prog.filesDone / prog.filesTotal) * 100) : 0;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-indigo-300">Syncing to NAS…</span>
        <span className="text-slate-400">{pct}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${INDIGO}, ${FUCHSIA})` }}
        />
      </div>
      <div className="flex items-center justify-between gap-3 text-xs text-slate-400">
        <span>
          {prog.filesDone} / {prog.filesTotal} files · {humanBytes(prog.bytesDone)} /{' '}
          {humanBytes(prog.bytesTotal)}
        </span>
        <span className="min-w-0 truncate text-slate-500">{prog.currentFile ?? '…'}</span>
      </div>
      <Button
        variant="danger"
        type="button"
        className="w-full"
        disabled={sync.cancelling}
        onClick={sync.cancel}
      >
        {sync.cancelling ? 'Cancelling…' : 'Cancel'}
      </Button>
      {sync.error && <p className="text-sm text-rose-400">{sync.error}</p>}
    </div>
  );
}

function WritableIndicator({ reachable, writable }: { reachable: boolean; writable: boolean }) {
  if (writable) return <span className="text-emerald-400">writable ✓</span>;
  if (reachable) return <span className="text-amber-400">not writable</span>;
  return <span className="text-amber-400">not mounted / not reachable</span>;
}

function SegmentedMode({
  value,
  onChange,
  disabled,
}: {
  value: NasMode;
  onChange: (m: NasMode) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex rounded-md border border-slate-700 bg-slate-950 p-0.5">
      {NAS_MODES.map((m) => (
        <button
          key={m}
          type="button"
          disabled={disabled}
          onClick={() => onChange(m)}
          className={`rounded px-4 py-1.5 text-sm font-medium capitalize transition disabled:opacity-50 ${
            value === m ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-800'
          }`}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

// ── Directory picker (jailed browse over /api/nas/browse) ───────────────────────
function BrowseModal({
  initialPath,
  onClose,
  onSelect,
}: {
  initialPath: string;
  onClose: () => void;
  onSelect: (path: string) => void;
}) {
  // `undefined` path = start at the NAS root (server jails it).
  const [path, setPath] = useState<string | undefined>(initialPath || undefined);
  const browse = useQuery({
    queryKey: ['nas-browse', path ?? '(root)'],
    queryFn: () =>
      api.get<NasBrowseResult>(`/api/nas/browse${path ? `?path=${encodeURIComponent(path)}` : ''}`),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-800 px-4 py-3">
          <div className="text-sm font-semibold text-slate-200">Choose a destination folder</div>
          <div className="mt-1 truncate text-xs text-slate-500" title={browse.data?.path}>
            {browse.data?.path ?? '…'}
          </div>
        </div>

        <div className="min-h-[12rem] flex-1 overflow-y-auto p-2">
          {browse.isLoading && <Spinner label="Reading folder…" />}
          {browse.isError && (
            <ErrorState message="Couldn't read that folder." onRetry={() => browse.refetch()} />
          )}
          {browse.data && (
            <ul className="space-y-0.5 text-sm">
              {browse.data.parent !== null && (
                <li>
                  <button
                    type="button"
                    onClick={() => setPath(browse.data!.parent ?? undefined)}
                    className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-left text-slate-300 hover:bg-slate-800"
                  >
                    <span className="text-slate-500">↰</span> .. up
                  </button>
                </li>
              )}
              {browse.data.dirs.map((d) => (
                <li key={d.path}>
                  <button
                    type="button"
                    onClick={() => setPath(d.path)}
                    className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-left text-slate-200 hover:bg-slate-800"
                  >
                    <span className="text-indigo-400">📁</span>
                    <span className="truncate">{d.name}</span>
                  </button>
                </li>
              ))}
              {browse.data.dirs.length === 0 && (
                <li className="px-3 py-2 text-xs text-slate-500">No sub-folders here.</li>
              )}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-800 px-4 py-3">
          <span className="text-xs">
            {browse.data &&
              (browse.data.writable ? (
                <span className="text-emerald-400">writable ✓</span>
              ) : (
                <span className="text-amber-400">not writable</span>
              ))}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!browse.data}
              onClick={() => browse.data && onSelect(browse.data.path)}
            >
              Use this folder
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Icons (inline SVG) ─────────────────────────────────────────────────────────
function FolderIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="ls-folder" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#818cf8" />
          <stop offset="1" stopColor={INDIGO} />
        </linearGradient>
      </defs>
      <path
        d="M6 14a3 3 0 0 1 3-3h9l3 4h15a3 3 0 0 1 3 3v18a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3V14Z"
        fill="url(#ls-folder)"
      />
      <rect x="6" y="18" width="36" height="22" rx="3" fill={INDIGO} opacity="0.35" />
      <circle cx="24" cy="29" r="3.5" fill="#c7d2fe" />
    </svg>
  );
}

function NasIcon({ reachable }: { reachable: boolean }) {
  const c = reachable ? FUCHSIA : '#f59e0b';
  const lit = reachable ? '#f0abfc' : '#fbbf24';
  return (
    <svg width="44" height="44" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <rect x="10" y="8" width="28" height="32" rx="3" fill="#1e293b" stroke={c} strokeWidth="1.5" />
      {[12, 20, 28].map((y) => (
        <g key={y}>
          <rect x="14" y={y} width="20" height="6" rx="1.5" fill="#0f172a" stroke={c} strokeWidth="1" />
          <circle cx="30" cy={y + 3} r="1.3" fill={lit} />
        </g>
      ))}
    </svg>
  );
}
